import React, { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Box, Button, ButtonBase, MenuItem, TextField, Typography } from "@mui/material";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
  fetchClassCertificateTypes, getAhaCatalog, getAhaClassSetup, saveAhaClassConfig, saveClassCertificateTypes,
} from "../../api/apiClient";
import AhaCoursePicker from "./AhaCoursePicker";
import AhaIdentifiersDialog from "./AhaIdentifiersDialog";
import { computeSetupSteps, setupChip } from "./ahaSetupSteps";
import { AHA_TOKENS as T, FOCUS_RING } from "./ahaTokens";
import { isTestEnvironment } from "./ahaText";

const SUBCOURSES = [
  { id: 1, name: "Prehospital" },
  { id: 2, name: "In-hospital" },
  { id: 3, name: "Prehospital & In-hospital" },
];

const STATE_CHIP = {
  done: { label: "Done", color: T.success, bg: T.successBg, border: "rgba(21,128,61,.2)", Icon: CheckIcon },
  you: { label: "Needs you", color: "#fff", bg: T.ink, border: "transparent", Icon: ArrowForwardIcon },
  admin: { label: "Needs an admin", color: T.warn, bg: T.warnBg, border: "rgba(194,65,12,.2)", Icon: AdminPanelSettingsOutlinedIcon },
  lock: { label: "Locked", color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: LockOutlinedIcon },
  na: { label: "Not applicable", color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: RemoveIcon },
};

const CHIP_TONE = {
  lock: { color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: LockOutlinedIcon },
  pause: { color: T.muted, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: PauseCircleOutlineIcon },
  admin: { color: T.warn, bg: T.warnBg, border: "rgba(194,65,12,.2)", Icon: AdminPanelSettingsOutlinedIcon },
  ready: { color: T.limeInk, bg: "rgba(194,239,78,.45)", border: "rgba(110,140,20,.35)", Icon: CheckIcon },
  plain: { color: T.ink, bg: "rgba(121,98,140,.1)", border: "rgba(121,98,140,.18)", Icon: null },
};

const chipSx = ({ color, bg, border }) => ({ display: "inline-flex", alignItems: "center", gap: "6px", height: 26, px: "10px",
  borderRadius: 999, fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", color, bgcolor: bg, border: `1px solid ${border}`,
  "& svg": { fontSize: 14 } });

const NEXT_TEXT = {
  enable: "turn on AHA eCards",
  course: "choose the AHA course",
  instructor: "add the class’s instructor",
};

const formatDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const errorText = (e, fallback) => e?.response?.data?.message || fallback;

function StateChip({ state, label }) {
  const s = STATE_CHIP[state];
  return <Box component="span" sx={chipSx(s)}><s.Icon />{label || s.label}</Box>;
}

function NextLabel() {
  return (
    <Box component="span" sx={{ fontSize: 10, fontWeight: 600, letterSpacing: ".25px", textTransform: "uppercase", color: T.limeInk,
      bgcolor: T.lime, px: "7px", py: "2px", borderRadius: "4px" }}>Next</Box>
  );
}

function AskAdmin({ admin, subject, children }) {
  const href = admin?.email
    ? `mailto:${admin.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(`Hi ${admin.name},\n\n${subject}. Could you take a look?\n\nThanks`)}`
    : null;
  return (
    <Box sx={{ mt: "10px", p: "12px 14px", borderRadius: "8px", bgcolor: T.press, display: "flex", gap: "12px", alignItems: "flex-start", maxWidth: 560 }}>
      <InfoOutlinedIcon sx={{ fontSize: 18, mt: "2px", color: T.violet, flexShrink: 0 }} />
      <Box>
        {children}
        {href && (
          <Box sx={{ mt: "12px" }}>
            <Button component="a" href={href} size="small" variant="outlined" startIcon={<MailOutlineIcon />}
              sx={{ color: T.ink, borderColor: T.hairlineCool, fontWeight: 700 }}>{`Email ${admin.name}`}</Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * The class's AHA eCard setup on the Details tab: one checklist that says whether the class is ready to issue AHA
 * eCards and, if not, what is next, who does it and where. Coordinators and admins edit; everyone else sees it
 * read-only. Everything is derived from the server's setup view (see ahaSetupSteps).
 */
export default function AhaSetupCard({ classId, canEdit, refreshKey, onChanged, standard = false }) {
  const [setup, setSetup] = useState(null);
  const [types, setTypes] = useState([]);
  const [available, setAvailable] = useState([]);
  const [catalog, setCatalog] = useState(null);
  const [error, setError] = useState(false);
  const [open, setOpen] = useState([]);
  const [busy, setBusy] = useState(false);
  const [switchError, setSwitchError] = useState("");
  const [courseError, setCourseError] = useState("");
  const [justChose, setJustChose] = useState(false);
  const [pending, setPending] = useState(null);
  const [subcourseId, setSubcourseId] = useState("");
  const [identifiersOpen, setIdentifiersOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [view, selection, cat] = await Promise.all([
        getAhaClassSetup(classId),
        fetchClassCertificateTypes(classId).catch(() => null),
        getAhaCatalog().catch(() => null),
      ]);
      setSetup(view);
      setTypes(selection?.types || []);
      setAvailable(selection?.available || []);
      setCatalog(cat);
      setError(false);
    } catch {
      setError(true);
    }
  }, [classId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const result = setup ? computeSetupSteps(setup) : null;
  const current = result?.current || null;
  // Open the step to work on; when only an admin can act, open the first step waiting on them so the "who to ask" shows.
  const focusKey = current || result?.steps.find((s) => s.state === "admin")?.key || null;
  // Layout effect so the step is already open on the first painted render (no closed-step flash, no test race).
  useLayoutEffect(() => {
    if (focusKey) setOpen((o) => (o.includes(focusKey) ? o : [...o, focusKey]));
  }, [focusKey]);

  if (error) return <Alert severity="warning">The class{"’"}s AHA eCard setup couldn{"’"}t be loaded.</Alert>;
  if (!setup) return null;

  const { steps, course, sessionsDone, sessionsTotal, missingInstructors, locked } = result;
  const editable = !!canEdit && !locked;
  const admin = setup.centerAdmin || null;
  const adminName = admin?.name || "An admin";
  const chip = setupChip(result);
  const tone = CHIP_TONE[chip.tone];
  const missingSessions = setup.sessionsMissingModules || [];
  const sessionRows = setup.sessions || [];
  const sessionNumber = (id) => sessionRows.find((s) => s.id === id)?.number ?? (missingSessions.findIndex((s) => s.id === id) + 1);
  const firstMissing = missingSessions[0] || null;
  const editSessionPath = (id) => `/course-management/${classId}/sessions?edit=${id}`;
  const products = catalog?.products || [];
  const environment = setup.environment || "";

  const toggle = (key) => setOpen((o) => (o.includes(key) ? o.filter((k) => k !== key) : [...o, key]));
  const focusStep = (key) => document.getElementById(`aha-step-btn-${key}`)?.focus();

  const afterSave = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const toggleClass = async (on) => {
    setSwitchError("");
    setBusy(true);
    try {
      const next = on ? [...types, "AHA"] : types.filter((t) => t !== "AHA");
      const order = available.map((a) => a.type);
      await saveClassCertificateTypes(classId, [...new Set(next)].sort((a, b) => order.indexOf(a) - order.indexOf(b)));
      await afterSave();
    } catch (e) {
      setSwitchError(errorText(e, "The class’s certificates couldn’t be saved."));
    } finally {
      setBusy(false);
    }
  };

  const saveCourse = async (product, subcourse) => {
    setCourseError("");
    setBusy(true);
    try {
      await saveAhaClassConfig(classId, { productKey: product.key, moduleKeys: [], subcourseId: subcourse });
      setPending(null);
      setSubcourseId("");
      setJustChose(true);
      await afterSave();
    } catch (e) {
      setCourseError(errorText(e, "The AHA course couldn’t be saved."));
    } finally {
      setBusy(false);
    }
  };

  const chooseCourse = (product) => {
    setCourseError("");
    setJustChose(false);
    if (product.requiresSubcourse) { setPending(product); setSubcourseId(""); return; }
    setPending(null);
    saveCourse(product, null);
  };

  const nextLabel = current === "modules"
    ? `add modules to session ${firstMissing ? sessionNumber(firstMissing.id) : 1}`
    : NEXT_TEXT[current];

  const ownerText = (s) => {
    if (s.state === "done" || s.state === "na" || s.state === "lock") return "";
    if (s.owner === "you") return "You";
    return admin ? `Admin: ${admin.name}` : "Admin";
  };

  const body = (s) => {
    switch (s.key) {
      case "center":
        return setup.centerReady ? (
          <Typography sx={{ color: T.muted, fontSize: 14 }}>
            Set up by an admin in Training Center Settings {"›"} Certificates. Nothing for you to do here.
          </Typography>
        ) : (
          <AskAdmin admin={admin} subject="Please connect our training center to the AHA">
            <Typography sx={{ fontSize: 14 }}>
              Your training center isn{"’"}t connected to the AHA yet, so no class can issue AHA eCards.{" "}
              <b>{adminName}</b> (admin) can do this in Training Center Settings {"›"} Certificates.
            </Typography>
          </AskAdmin>
        );
      case "enable":
        return (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mt: "6px" }}>
              <ButtonBase role="switch" aria-checked={!!setup.classIssuesAha} aria-label="Issue AHA eCards"
                disabled={!editable || !setup.centerReady || busy} onClick={() => toggleClass(!setup.classIssuesAha)}
                sx={{ width: 44, height: 26, borderRadius: "13px", p: "3px", justifyContent: setup.classIssuesAha ? "flex-end" : "flex-start",
                  bgcolor: setup.classIssuesAha ? T.ink : T.hairlineCool, transition: "background .15s", flexShrink: 0,
                  "&.Mui-disabled": { opacity: 0.5 }, "&.Mui-focusVisible": FOCUS_RING }}>
                <Box component="span" sx={{ width: 20, height: 20, borderRadius: "50%", bgcolor: "#fff", boxShadow: "0 1px 2px rgba(0,0,0,.2)" }} />
              </ButtonBase>
              <Typography component="span" sx={{ fontWeight: 500, fontSize: 14 }}>Issue AHA eCards when students pass this class</Typography>
            </Box>
            {switchError && <Alert severity="error" sx={{ mt: 1.5, maxWidth: 560 }}>{switchError}</Alert>}
            {s.state === "you" && (
              <Typography sx={{ mt: 1, color: T.muted, fontSize: 14 }}>
                The certificate of completion is set separately on the Roster tab.
              </Typography>
            )}
          </Box>
        );
      case "course": {
        const shown = pending || course;
        return (
          <Box sx={{ maxWidth: 560 }}>
            <AhaCoursePicker products={products} selectedKey={shown?.productKey || shown?.key || null}
              readOnly={!editable} disabled={busy} onSelect={chooseCourse} />
            {canEdit && course?.productKey && (
              <Button size="small" sx={{ mt: 1 }} onClick={() => setIdentifiersOpen(true)}>
                Update AHA product and module IDs
              </Button>
            )}
            {pending && (
              <Box sx={{ mt: 1.5, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                <TextField select size="small" label="ASLS subcourse" value={subcourseId} sx={{ minWidth: 240 }}
                  onChange={(e) => setSubcourseId(e.target.value)}>
                  {SUBCOURSES.map((sc) => <MenuItem key={sc.id} value={String(sc.id)}>{sc.name}</MenuItem>)}
                </TextField>
                <Button variant="contained" size="small" disabled={!subcourseId || busy}
                  onClick={() => saveCourse(pending, Number(subcourseId))}>Save course</Button>
              </Box>
            )}
            {courseError && <Alert severity="error" sx={{ mt: 1.5 }}>{courseError}</Alert>}
            {justChose && course && !pending && (
              <Box sx={{ mt: "12px", p: "14px 16px", borderRadius: "10px", bgcolor: T.subtle, border: `1px solid ${T.hairlineCloud}`, fontSize: 14, lineHeight: 1.5 }}>
                {setup.productHasModules ? (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, fontSize: 15, mb: "4px" }}>
                      <InfoOutlinedIcon sx={{ fontSize: 18, color: T.violet }} />
                      This course uses modules, so add them to your sessions
                    </Box>
                    <div>Each session needs to list which parts of the course it covers. {sessionsDone} of {sessionsTotal} sessions done.</div>
                    {editable && firstMissing && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: "14px", mt: "12px" }}>
                        <NextLabel />
                        <Button component={RouterLink} to={editSessionPath(firstMissing.id)} variant="contained" size="small" endIcon={<ArrowForwardIcon />}>
                          {`Add modules to session ${sessionNumber(firstMissing.id)}`}
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600, fontSize: 15, mb: "4px" }}>
                      <CheckIcon sx={{ fontSize: 18, color: T.success }} />No modules needed
                    </Box>
                    This course doesn{"’"}t use modules, so your sessions are ready as they are. Next, check the instructor.
                  </>
                )}
              </Box>
            )}
          </Box>
        );
      }
      case "modules": {
        if (!course || !setup.productHasModules) return <Typography sx={{ color: T.muted, fontSize: 14 }}>{s.summary}</Typography>;
        const missingIds = new Set(missingSessions.map((m) => m.id));
        return (
          <Box>
            <Box sx={{ display: "grid", gap: "6px", mt: "10px", maxWidth: 560 }}>
              {sessionRows.map((row) => {
                const ok = !missingIds.has(row.id);
                return (
                  <Box key={row.id} sx={{ display: "grid", gridTemplateColumns: "22px 1fr auto", gap: "10px", alignItems: "center", p: "9px 12px",
                    border: `1px solid ${T.hairlineCloud}`, borderRadius: "8px", bgcolor: "#fff" }}>
                    {ok ? <CheckIcon sx={{ fontSize: 18, color: T.success }} /> : <WarningAmberIcon sx={{ fontSize: 18, color: T.warn }} />}
                    <Box>
                      <b style={{ fontWeight: 600 }}>{`Session ${row.number}`}</b>
                      {row.date && <span style={{ color: T.muted }}>{` · ${formatDate(row.date)}`}</span>}
                      <div style={{ fontSize: 13, color: ok ? T.muted : "inherit" }}>
                        {ok ? (row.moduleNames?.length ? row.moduleNames.join(", ") : "Modules set for the whole class") : "No modules added"}
                      </div>
                    </Box>
                    {editable && (ok || row.id !== firstMissing?.id || s.state !== "you") && (
                      <Box component={RouterLink} to={editSessionPath(row.id)}
                        sx={{ fontSize: 14, fontWeight: 600, color: T.violet, textDecoration: "none", "&:hover": { color: T.ink } }}>
                        {ok ? "Edit" : "Add modules"}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
            {editable && s.state === "you" && firstMissing && (
              <Box sx={{ display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", mt: "12px" }}>
                <NextLabel />
                <Button component={RouterLink} to={editSessionPath(firstMissing.id)} variant="contained" size="small" endIcon={<ArrowForwardIcon />}>
                  {`Fix session ${sessionNumber(firstMissing.id)}`}
                </Button>
                <Typography component="span" sx={{ color: T.muted, fontSize: 13 }}>
                  {`Opens session ${sessionNumber(firstMissing.id)} on the Sessions tab`}
                </Typography>
              </Box>
            )}
          </Box>
        );
      }
      case "instructor":
        return (
          <Box>
            <Box sx={{ display: "grid", gap: "6px", mt: "10px", maxWidth: 560 }}>
              {(setup.instructors || []).map((i) => (
                <Box key={i.userId} sx={{ display: "grid", gridTemplateColumns: "32px 1fr auto", gap: "12px", alignItems: "center", p: "9px 12px",
                  border: `1px solid ${T.hairlineCloud}`, borderRadius: "8px", bgcolor: "#fff" }}>
                  <Box sx={{ width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 12, fontWeight: 600, color: "#fff", bgcolor: T.muted }}>
                    {i.name.split(/\s+/).map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                  </Box>
                  <Box><b style={{ fontWeight: 600 }}>{i.name}</b><div style={{ fontSize: 13, color: T.muted }}>Class instructor</div></Box>
                  {i.hasAhaId
                    ? <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 13, fontWeight: 600, color: T.success }}><CheckIcon sx={{ fontSize: 15 }} />Has ID</Box>
                    : <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: "5px", fontSize: 13, fontWeight: 600, color: T.warn }}><WarningAmberIcon sx={{ fontSize: 15 }} />Missing</Box>}
                </Box>
              ))}
            </Box>
            {(setup.instructors || []).length === 0 && !locked && (
              <Typography sx={{ mt: 1, fontSize: 14 }}>Assign an instructor on the Instructors tab.</Typography>
            )}
            {missingInstructors.length > 0 && editable && (
              <AskAdmin admin={admin} subject={`Please add an AHA instructor ID for ${missingInstructors[0].name}`}>
                <Typography sx={{ fontSize: 14 }}>
                  Cards are issued under the class instructor{"’"}s AHA ID. You don{"’"}t need one yourself. Ask <b>{adminName}</b> (admin) to add{" "}
                  {missingInstructors[0].name}{"’"}s ID in Instructor Management, or assign a different instructor on the Instructors tab.
                </Typography>
              </AskAdmin>
            )}
          </Box>
        );
      default:
        return null;
    }
  };

  const banner = (icon, title, text, tint) => (
    <Box sx={{ display: "flex", gap: "14px", alignItems: "flex-start", m: "16px 22px 4px", p: "14px 16px", borderRadius: "10px", fontSize: 14, lineHeight: 1.5,
      bgcolor: tint || T.subtle, border: `1px solid ${tint ? "rgba(21,128,61,.2)" : T.hairlineCloud}` }}>
      <Box sx={{ mt: "1px", display: "flex" }}>{icon}</Box>
      <Box><Box sx={{ fontWeight: 700, fontSize: 15, mb: "2px" }}>{title}</Box>{text}</Box>
    </Box>
  );

  return (
    <Box component="section" aria-labelledby="aha-setup-heading" data-testid="aha-setup-card"
      sx={{ border: `1px solid ${T.hairlineCloud}`, borderRadius: "12px", bgcolor: "#fff", boxShadow: T.card, color: T.ink }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: "12px", p: "18px 22px", borderBottom: `1px solid ${T.hairlineCloud}`, flexWrap: "wrap" }}>
        <Typography id="aha-setup-heading" component="h2" sx={{ m: 0, fontFamily: "'Space Grotesk', inherit", fontSize: 20, fontWeight: 600 }}>
          AHA eCard setup
        </Typography>
        <Box component="span" sx={chipSx(tone)}>{tone.Icon && <tone.Icon />}{chip.label}</Box>
        {isTestEnvironment(environment) && (
          <Box component="span" title="Test environment. Cards are not real."
            sx={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, fontWeight: 700, px: "7px", py: "3px", borderRadius: "4px",
              bgcolor: T.ink, color: "#fff", letterSpacing: ".5px" }}>{environment}</Box>
        )}
        <Box sx={{ flex: 1 }} />
        {current && editable && (
          <ButtonBase data-testid="aha-setup-next" onClick={() => focusStep(current)}
            sx={{ display: "inline-flex", gap: "8px", fontSize: 14, fontWeight: 600, color: T.violet, "&:hover": { color: T.ink }, "&.Mui-focusVisible": FOCUS_RING }}>
            <NextLabel />
            <span>{nextLabel ? nextLabel.charAt(0).toUpperCase() + nextLabel.slice(1) : ""}</span>
            <ChevronRightIcon sx={{ fontSize: 16 }} />
          </ButtonBase>
        )}
        {!canEdit && !locked && (
          <Typography component="span" sx={{ color: T.muted, fontSize: 13 }}>
            {standard ? "This AHA setup comes from the standard course and cannot be changed here." : "View only. Coordinators and admins can change this."}
          </Typography>
        )}
      </Box>

      {locked && banner(<LockOutlinedIcon sx={{ fontSize: 18, color: T.muted }} />, "This setup is read-only",
        <>
          {setup.completedAt ? `The class was completed on ${formatDate(setup.completedAt)} and ` : "The class is completed and "}
          AHA eCards were issued with these settings. Changing them now wouldn{"’"}t match the AHA{"’"}s records.
          {" "}If a card needs correcting, contact {adminName} (admin).
        </>)}
      {!locked && result.sendingPaused && banner(<PauseCircleOutlineIcon sx={{ fontSize: 18, color: T.muted }} />,
        "AHA eCard sending is paused for the whole system",
        "This isn’t something you need to fix. Your setup is complete and saved, and cards will send once sending is switched back on.")}
      {!locked && !result.sendingPaused && chip.tone === "ready" && banner(<CheckIcon sx={{ fontSize: 18, color: T.success }} />,
        "This class is ready to issue AHA eCards",
        "When you mark the class complete, students who are signed off will get their eCard. You’ll review who’s getting one on the Roster tab before anything is sent.",
        T.successBg)}

      <Box component="ol" sx={{ listStyle: "none", m: 0, p: "10px 12px 12px" }}>
        {steps.map((s, i) => {
          const isOpen = open.includes(s.key);
          const dim = s.state === "na" || s.waiting;
          const owner = ownerText(s);
          const iconSx = {
            width: 32, height: 32, borderRadius: "50%", display: "grid", placeItems: "center", fontSize: 13, fontWeight: 700,
            border: `1px solid ${T.hairlineCool}`, bgcolor: "#fff", color: T.ink, "& svg": { fontSize: 16 },
            ...(s.state === "done" && { bgcolor: T.success, borderColor: T.success, color: "#fff" }),
            ...(s.state === "you" && { bgcolor: T.ink, borderColor: T.ink, color: "#fff" }),
            ...(s.state === "admin" && { borderColor: T.warn, color: T.warn }),
            ...((s.state === "na" || s.state === "lock") && { bgcolor: T.press, borderColor: T.hairlineCloud, color: T.muted }),
          };
          const StepIcon = s.state === "done" ? CheckIcon : s.state === "lock" ? LockOutlinedIcon : s.state === "admin" ? AdminPanelSettingsOutlinedIcon : null;
          return (
            <Box component="li" key={s.key} data-testid={`aha-step-${s.key}`} data-state={s.state}
              sx={{ position: "relative", borderRadius: "10px", "& + &": { mt: "2px" },
                ...(s.key === current && { bgcolor: T.subtle, boxShadow: `inset 0 0 0 1px ${T.hairlineCool}` }) }}>
              <ButtonBase id={`aha-step-btn-${s.key}`} aria-expanded={isOpen} aria-controls={`aha-step-body-${s.key}`} onClick={() => toggle(s.key)}
                sx={{ display: "grid", gridTemplateColumns: "36px minmax(0,1fr) auto", gap: "14px", alignItems: "center", width: "100%", textAlign: "left",
                  p: "12px", borderRadius: "10px", "&:hover": { bgcolor: s.key === current ? "transparent" : T.subtle }, "&.Mui-focusVisible": FOCUS_RING }}>
                <Box sx={iconSx}>{StepIcon ? <StepIcon /> : i + 1}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Box sx={{ fontSize: 16, fontWeight: 600, lineHeight: 1.3, color: dim ? T.muted : T.ink }}>{`${i + 1}. ${s.title}`}</Box>
                  <Box sx={{ fontSize: 14, mt: "2px", lineHeight: 1.4, opacity: dim ? 1 : 0.78, color: dim ? T.muted : T.ink }}>{s.summary}</Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  {owner && <Box component="span" sx={{ fontSize: 12.5, color: T.muted, whiteSpace: "nowrap" }}>{owner}</Box>}
                  <StateChip state={s.state} label={s.waiting ? "Needs you · later" : undefined} />
                  <ExpandMoreIcon sx={{ fontSize: 18, color: T.muted, transform: isOpen ? "rotate(180deg)" : "none" }} />
                </Box>
              </ButtonBase>
              {isOpen && <Box id={`aha-step-body-${s.key}`} sx={{ p: "2px 16px 18px 62px", fontSize: 14, lineHeight: 1.5 }}>{body(s)}</Box>}
            </Box>
          );
        })}
      </Box>

      {setup.details && (
        <Box component="details" sx={{ borderTop: `1px solid ${T.hairlineCloud}`, p: "12px 22px" }}>
          <Box component="summary" sx={{ fontSize: 13, fontWeight: 600, color: T.muted, cursor: "pointer" }}>Details for admins</Box>
          <Box component="dl" sx={{ display: "grid", gridTemplateColumns: "200px 1fr", gap: "6px 16px", mt: "10px", fontSize: 13, m: 0 }}>
            {[
              ["Environment", environment || "not set"],
              ["AHA Training Center ID", setup.details.tcId || "not set"],
              ["AHA LMS ID", setup.details.lmsId || "not set"],
              ["productKey", setup.details.productKey || "not set"],
              ["Server sending", setup.sendingEnabled ? "on" : "off (AHA_DISPATCH_ENABLED=false)"],
            ].map(([k, v]) => (
              <React.Fragment key={k}>
                <Box component="dt" sx={{ color: T.muted }}>{k}</Box>
                <Box component="dd" sx={{ m: 0, fontFamily: "'JetBrains Mono', monospace", fontSize: 12.5 }}>{v}</Box>
              </React.Fragment>
            ))}
          </Box>
        </Box>
      )}
      <AhaIdentifiersDialog open={identifiersOpen} onClose={() => setIdentifiersOpen(false)}
        productKey={course?.productKey} onChanged={load} />
    </Box>
  );
}
